import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Rating,
} from "@mui/material"
import { Search, LocationOn, Visibility, CalendarToday } from "@mui/icons-material"
import { fetchAllBathroomsAdmin } from "../services/adminService"
import { format } from "date-fns"

function AdminBathrooms() {
  const navigate = useNavigate()
  const [bathrooms, setBathrooms] = useState([])
  const [filteredBathrooms, setFilteredBathrooms] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)

  useEffect(() => {
    loadBathrooms()
  }, [])

  useEffect(() => {
    // Filter bathrooms based on search query
    if (searchQuery.trim() === "") {
      setFilteredBathrooms(bathrooms)
    } else {
      const query = searchQuery.toLowerCase()
      const filtered = bathrooms.filter(
        bathroom =>
          (bathroom.name && bathroom.name.toLowerCase().includes(query)) ||
          (bathroom.locationName && bathroom.locationName.toLowerCase().includes(query)) ||
          (bathroom.address && bathroom.address.toLowerCase().includes(query)),
      )
      setFilteredBathrooms(filtered)
    }
    setPage(0)
  }, [searchQuery, bathrooms])

  const loadBathrooms = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchAllBathroomsAdmin()
      setBathrooms(data)
      setFilteredBathrooms(data)
    } catch (err) {
      console.error("Error loading bathrooms:", err)
      setError("Failed to load bathrooms. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleViewBathroom = bathroomId => {
    navigate(`/bathroom/${bathroomId}`)
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={60} />
        </Box>
      </Container>
    )
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    )
  }

  const paginatedBathrooms = filteredBathrooms.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
          🚽 Bathrooms Management
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Total Bathrooms: {bathrooms.length.toLocaleString()}
        </Typography>
      </Box>

      {/* Search Bar */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Search bathrooms by name, location, or address..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {/* Bathrooms Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell>Reviews</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedBathrooms.map(bathroom => (
                <TableRow key={`${bathroom.locationId}/${bathroom.id}`} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {bathroom.name || bathroom.locationName || "Unnamed"}
                    </Typography>
                    {bathroom.genderType && <Chip label={bathroom.genderType} size="small" sx={{ mt: 0.5 }} />}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <LocationOn fontSize="small" color="action" />
                      <Box>
                        <Typography variant="body2">{bathroom.locationName || "Unknown"}</Typography>
                        {bathroom.address ? (
                          <Typography
                            component="a"
                            href={
                              bathroom.latitude && bathroom.longitude
                                ? `https://www.google.com/maps/search/?api=1&query=${bathroom.latitude},${bathroom.longitude}`
                                : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(bathroom.address)}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="caption"
                            sx={{
                              color: "primary.main",
                              textDecoration: "none",
                              "&:hover": { textDecoration: "underline" },
                              whiteSpace: "pre-line",
                            }}
                          >
                            {bathroom.address.replace(/,\s*/, ",\n")}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            No address
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {bathroom.ratings?.overall ? (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Rating value={bathroom.ratings.overall} precision={0.1} size="small" readOnly />
                        <Typography variant="body2">{bathroom.ratings.overall.toFixed(1)}</Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No rating
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{bathroom.reviewCount || 0}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <CalendarToday fontSize="small" color="action" />
                      <Typography variant="body2">{bathroom.createdAt ? format(bathroom.createdAt, "MMM d, yyyy") : "N/A"}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleViewBathroom(`${bathroom.locationId}/${bathroom.id}`)} color="primary">
                      <Visibility />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredBathrooms.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Card>
    </Container>
  )
}

export default AdminBathrooms
